import { useCallback, useMemo, useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { URLSearchParamsInit } from 'react-router-dom';

import type { OrganizationJob } from 'state/remote/types';

import usePaginatedOrganizationJobs from 'state/remote/remote-organization/use-paginated-organization-jobs';
import { getCurrentWeek } from 'utils/date';

import useCancelJobs from 'state/remote/remote-organization/use-cancel-jobs';

import type { OrganizationSearchParams } from './use-organization-planner-search-params';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;

export type TablePlannerParams = {
  currentPageNumber: number;
  currentPageSize: number;
  onStartDateChange: (value: Dayjs | undefined) => void;
  onEndDateChange: (value: Dayjs | undefined) => void;
  onPageChange: (page: number) => void;
  onNumberOfItemsPerPageChange: (numberOfItemsPerPage: number) => void;
  onSelectedItemsChange: (selectedJobs: Array<Record<string, unknown>>) => void;
  currentPage: Array<OrganizationJob>;
  currentPeriod: [Dayjs, Dayjs];
  totalPages: number;
  error?: string | null;
  isLoading?: boolean;
  numberOfSelectedJobs: number;
  selectedJobs: Array<number>;
  isCancelBulkJobsDialogOpen: boolean;
  setIsCancelBulkJobsDialogOpen: (open: boolean) => void;
  onCancelBulkJobsDialogAcceptClicked: (reason?: string) => void;
  displayCompensationOnBulkCancel: boolean;
};

export const tablePlannerParamsInitialValue: TablePlannerParams = {
  currentPage: [],
  currentPageNumber: DEFAULT_PAGE,
  currentPageSize: DEFAULT_PAGE_SIZE,
  currentPeriod: [getCurrentWeek()[0].add(-7, 'd'), getCurrentWeek()[0]],
  displayCompensationOnBulkCancel: false,
  isCancelBulkJobsDialogOpen: false,
  numberOfSelectedJobs: 0,
  onCancelBulkJobsDialogAcceptClicked: (_) => {},
  onEndDateChange: (_) => {},
  onNumberOfItemsPerPageChange: (_) => {},
  onPageChange: (_) => {},
  onSelectedItemsChange: (_) => {},
  onStartDateChange: (_) => {},
  selectedJobs: [],
  setIsCancelBulkJobsDialogOpen: (_) => {},
  totalPages: 0,
};

const useTablePlannerParams = (
  organizationSearchParams: OrganizationSearchParams,
  plannerSearchParams: {
    searchParams: URLSearchParams;
    setSearchParams: (
      nextInit: URLSearchParamsInit,
      navigateOptions?:
        | {
            replace?: boolean | undefined;
            state?: unknown;
          }
        | undefined,
    ) => void;
    currentSearchParams: Record<string, string>;
  },
): TablePlannerParams => {
  const { currentSearchParams, searchParams, setSearchParams } =
    plannerSearchParams;

  const currentPeriod: [Dayjs, Dayjs] = useMemo(() => {
    return [
      dayjs(searchParams.get('tableFromDate')),
      dayjs(searchParams.get('tableToDate')),
    ];
  }, [searchParams]);

  const onStartDateChange = useCallback(
    (value: Dayjs | undefined) => {
      if (value)
        setSearchParams({
          ...currentSearchParams,
          tableFromDate: value.format('YYYY-MM-DD'),
        });
    },
    [currentSearchParams, setSearchParams],
  );

  const onEndDateChange = useCallback(
    (value: Dayjs | undefined) => {
      if (value)
        setSearchParams({
          ...currentSearchParams,
          tableToDate: value.format('YYYY-MM-DD'),
        });
    },
    [currentSearchParams, setSearchParams],
  );

  const currentPageSize = useMemo(
    () =>
      parseInt(
        searchParams.get('pageSize') ?? DEFAULT_PAGE_SIZE.toString(),
        10,
      ),
    [searchParams],
  );
  const { selectedFreelancers, selectedLocations, selectedOrganizations } =
    organizationSearchParams;
  const currentPageNumber = useMemo(() => {
    return parseInt(
      searchParams.get('pageNumber') ?? DEFAULT_PAGE.toString(),
      10,
    );
  }, [searchParams]);

  const { data, error, isLoading, fetchNextPage, isFetching } =
    usePaginatedOrganizationJobs({
      assignedStatus: organizationSearchParams.jobStatus,
      fromDate: currentPeriod[0].format('YYYY-MM-DD'),
      jobType: organizationSearchParams.jobType,
      page: currentPageNumber,
      pageSize: currentPageSize,
      selectedFreelancers,
      selectedLocations,
      selectedOrganizations,
      toDate: currentPeriod[1].format('YYYY-MM-DD'),
    });

  const onNumberOfItemsPerPageChange = useCallback(
    (numberOfItemsPerPage: number) => {
      setSearchParams({
        ...currentSearchParams,
        pageNumber: DEFAULT_PAGE.toString(),
        pageSize: numberOfItemsPerPage.toString(),
      });
    },
    [currentSearchParams, setSearchParams],
  );

  const currentPage = useMemo(() => {
    const searchCurrentPage = data?.pages.find(
      (page) => page.pageNumber === currentPageNumber,
    );

    const processedJobs = searchCurrentPage?.results.map((job) => ({
      ...job,
      status: job.assigned_to ? 'assigned' : 'open',
      workHours: `${dayjs(job.start, 'HH:mm:ss').format('HH:mm')} - ${dayjs(
        job.end,
        'HH:mm:ss',
      ).format('HH:mm')}`,
    }));

    return processedJobs ?? [];
  }, [currentPageNumber, data?.pages]);

  const totalPages = useMemo(() => {
    return Math.ceil((data?.pages[0].count ?? 0) / currentPageSize);
  }, [currentPageSize, data?.pages]);

  const onPageChange = useCallback(
    async (page: number) => {
      if (data) {
        data.pages = data.pages.filter((p) => p.pageNumber !== page);
        data.pageParams = data.pageParams.filter((pp) => pp !== page);
      }
      await fetchNextPage({ pageParam: page });
      setSearchParams({
        ...currentSearchParams,
        pageNumber: page.toString(),
      });
    },
    [currentSearchParams, data, fetchNextPage, setSearchParams],
  );

  const [selectedJobs, setSelectedJobs] = useState<Array<number>>([]);

  const numberOfSelectedJobs = useMemo(() => {
    return selectedJobs.length;
  }, [selectedJobs]);

  const onSelectedItemsChange = useCallback(
    (newSelectedJobs: Array<Record<string, unknown>>) => {
      const selectedJobsIds = newSelectedJobs.map(
        (value: Record<string, unknown>) => {
          const job = value as unknown as OrganizationJob;
          return job.id;
        },
      );
      setSelectedJobs([...selectedJobsIds]);
    },
    [],
  );

  const cancelJobMutation = useCancelJobs();
  const [isCancelBulkJobsDialogOpen, setIsCancelBulkJobsDialogOpen] =
    useState(false);
  const onCancelBulkJobsDialogAcceptClicked = (reason?: string) => {
    cancelJobMutation.mutate({ jobs: selectedJobs, reason });
    setIsCancelBulkJobsDialogOpen(false);
  };
  const displayCompensationOnBulkCancel = useMemo(() => {
    let showCompensateMessage = false;
    const jobs = currentPage.filter((job) => selectedJobs.includes(job.id));
    const DAYJS_FORMAT = 'YYYY-MM-DD HH:mm:ss';

    jobs.forEach((job) => {
      const startDate = dayjs(`${job.date} ${job.start}`, DAYJS_FORMAT);
      const startDatetimeBeforeOneDay = startDate.add(-1, 'day');
      const startDatetimeAfterOneHour = startDate.add(1, 'h');

      if (
        job.assigned_to &&
        startDatetimeBeforeOneDay.isBefore(dayjs()) &&
        startDatetimeAfterOneHour.isAfter(dayjs())
      ) {
        showCompensateMessage = true;
      }
    });

    return showCompensateMessage;
  }, [currentPage, selectedJobs]);
  return {
    currentPage,
    currentPageNumber,
    currentPageSize,
    currentPeriod,
    displayCompensationOnBulkCancel,
    error,
    isCancelBulkJobsDialogOpen,
    isLoading: isLoading || isFetching,
    numberOfSelectedJobs,
    onCancelBulkJobsDialogAcceptClicked,
    onEndDateChange,
    onNumberOfItemsPerPageChange,
    onPageChange,
    onSelectedItemsChange,
    onStartDateChange,
    selectedJobs,
    setIsCancelBulkJobsDialogOpen,
    totalPages,
  };
};

export default useTablePlannerParams;
